import {
  Pagination,
  TablePagination as MuiTablePagination,
  useTheme,
  styled
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

export interface PaginationModel {
  page: number
  pageSize: number
}

export interface CustomTablePaginationProps {
  paginationModel: PaginationModel
  onPaginationModelChange: (model: PaginationModel) => void
  totalItems: number
  pageSizeOptions?: number[]
}

export function CustomTablePagination({
  paginationModel,
  onPaginationModelChange,
  totalItems,
  pageSizeOptions = [5, 10, 25, 50, 100]
}: Readonly<CustomTablePaginationProps>) {
  const { t } = useTranslation('_global')
  const theme = useTheme()

  const handlePageChange = (_event: unknown, newPage: number) => {
    onPaginationModelChange({ ...paginationModel, page: newPage })
  }

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = Number.parseInt(event.target.value, 10)
    onPaginationModelChange({ page: 0, pageSize: newPageSize })
  }

  const totalPages = Math.ceil(totalItems / paginationModel.pageSize)

  const DropDownIcon = styled(KeyboardArrowDownIcon)(({ theme }) => ({
    fontSize: 18,
    color: theme.palette.grey[600],
    marginRight: theme.spacing(1)
  }))

  const ActionsComponent = () => (
    <Pagination
      count={totalPages}
      page={paginationModel.page + 1}
      onChange={(event, page) => handlePageChange(event, page - 1)}
      shape="rounded"
      sx={{
        '& .MuiPaginationItem-root.Mui-selected': {
          backgroundColor: theme.palette.primary.main,
          color: 'white',
          '&:hover': {
            backgroundColor: theme.palette.primary.main
          }
        }
      }}
    />
  )

  return (
    <MuiTablePagination
      ActionsComponent={ActionsComponent}
      sx={{
        '.MuiTablePagination-toolbar': {
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          padding: theme.spacing(0.125)
        },
        '.MuiTablePagination-displayedRows': {
          order: 0,
          flex: '0 0 auto',
          marginRight: 'auto',
          color: theme.palette.grey[700],
          ...theme.typography.body3
        },
        '.MuiTablePagination-actions': {
          order: 2,
          flex: '0 0 auto',
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)'
        },
        '.MuiTablePagination-selectLabel': {
          order: 3,
          marginLeft: 'auto',
          color: theme.palette.grey[700],
          ...theme.typography.body3
        },
        '.MuiTablePagination-select': {
          order: 3,
          margin: 0,
          marginRight: 0,
          fontSize: theme.typography.body2.fontSize,
          color: theme.palette.grey[700]
        },
        '.MuiTablePagination-spacer': {
          display: 'none'
        },
        '.MuiPaginationItem-previousNext': {
          width: 34,
          height: 34,
          border: '1px solid '
        },
        '& .MuiPaginationItem-icon': {
          color: theme.palette.primary.main
        },
        '.MuiInputBase-colorPrimary.MuiTablePagination-select': {
          height: 34,
          border: '1px solid ',
          borderColor: theme.palette.grey[600],
          borderRadius: theme.shape.borderRadius,
          marginLeft: theme.spacing(1),
          paddingRight: theme.spacing(2),
          width: '80px'
        },
        '.MuiSelect-select-MuiInputBase-input': {
          padding: 0,
          display: 'flex',
          justifyContent: 'center'
        }
      }}
      rowsPerPageOptions={pageSizeOptions}
      component="div"
      count={totalItems}
      rowsPerPage={paginationModel.pageSize}
      page={paginationModel.page}
      onPageChange={handlePageChange}
      onRowsPerPageChange={handleRowsPerPageChange}
      labelDisplayedRows={({ to, count }) =>
        `${t('components.Table.showing')} ${to} ${t('components.Table.of')} ${count}`
      }
      labelRowsPerPage={t('components.Table.paginationRowsPerPage')}
      slotProps={{
        select: {
          IconComponent: DropDownIcon
        }
      }}
    />
  )
}
